import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PlusCircle, Sparkle, DotsThreeVertical, PencilSimple, Copy, Eye, EyeSlash, XCircle, UploadSimple, ShieldCheck } from "@phosphor-icons/react";
import { PageLoader, EmptyState, StatusBadge, SmartImage } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { toast } from "sonner";
import api, { money, apiErr } from "../../lib/api";
import { useAutoRefresh } from "../../hooks/useAutoRefresh";
import { RefreshBar } from "../../components/RefreshBar";

const MOD_STYLES = {
  approved: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/25", flagged: "bg-amber-500/15 text-amber-300 border border-amber-500/25",
  rejected: "bg-red-500/15 text-red-300 border border-red-500/25", pending: "bg-zinc-500/15 text-zinc-300 border border-zinc-500/25",
};

export default function MyEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = () => api.get("/organizer/events").then((r) => setEvents(r.data.events)).finally(() => setLoading(false));
  const { refreshing, lastUpdated, refresh } = useAutoRefresh(load, []);

  const act = async (id, action) => {
    try {
      if (action === "duplicate") { const { data } = await api.post(`/organizer/events/${id}/duplicate`); toast.success("Event duplicated."); navigate(`/organizer/events/${data.id}/edit`); return; }
      const { data } = await api.post(`/organizer/events/${id}/${action}`);
      toast.success(data.message); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const submitReview = async (id) => {
    try {
      const { data } = await api.post(`/organizer/events/${id}/submit-review`);
      const m = data.moderation || {};
      if (data.moderation_status === "approved") toast.success("AI review passed — you can publish now.");
      else toast.warning(`AI flagged this event: ${(m.reasons || []).join("; ") || m.summary || "Needs a human check."}`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <RefreshBar refreshing={refreshing} lastUpdated={lastUpdated} onRefresh={refresh} />
      <div className="flex items-center justify-between mb-6">
        <div><div className="overline mb-1">Manage</div><h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">My Events</h1></div>
        <div className="flex gap-2">
          <Button asChild variant="outline" className="rounded-full border-primary/30 text-primary" data-testid="ai-create-btn"><Link to="/organizer/events/new?ai=1"><Sparkle weight="fill" className="mr-1.5" /> AI Create</Link></Button>
          <Button asChild className="rounded-full" data-testid="create-event-btn"><Link to="/organizer/events/new"><PlusCircle className="mr-1.5" /> Create Event</Link></Button>
        </div>
      </div>

      {loading ? <PageLoader /> : events.length === 0 ? (
        <EmptyState icon={PlusCircle} title="No events yet" subtitle="Create your first event manually or with AI."
          action={<Button asChild className="rounded-full"><Link to="/organizer/events/new">Create event</Link></Button>} testid="no-org-events" />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {events.map((e) => (
            <div key={e.id} data-testid={`org-event-${e.id}`} className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="relative">
                <SmartImage src={e.banner} alt={e.title} ratio="video" rounded="rounded-none" />
                <span className="absolute top-2 left-2 z-[2]"><StatusBadge status={e.status} /></span>
                {e.moderation_status && e.moderation_status !== "approved" && (
                  <span className={`absolute top-2 right-2 z-[2] inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${MOD_STYLES[e.moderation_status] || "bg-muted"}`} data-testid={`mod-badge-${e.id}`}>
                    <ShieldCheck size={12} /> AI: {e.moderation_status}
                  </span>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-heading font-semibold tracking-tight truncate">{e.title}</h3>
                <div className="text-sm text-muted-foreground mt-1">{(e.cities || []).join(", ") || "—"} · {e.occurrence_count || 1} show{(e.occurrence_count || 1) > 1 ? "s" : ""}</div>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-sm">{e.sold}/{e.capacity} sold</span>
                  <div className="flex items-center gap-1">
                    <Button asChild size="sm" variant="outline" className="h-8"><Link to={`/organizer/events/${e.id}/edit`}><PencilSimple size={14} className="mr-1" /> Edit</Link></Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><button className="w-8 h-8 grid place-items-center rounded-lg border border-border" data-testid={`event-menu-${e.id}`}><DotsThreeVertical size={16} /></button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {e.status !== "published" && <DropdownMenuItem onClick={() => submitReview(e.id)} data-testid={`submit-review-${e.id}`}><ShieldCheck size={15} className="mr-2" /> Submit for AI review</DropdownMenuItem>}
                        {e.status !== "published" && <DropdownMenuItem onClick={() => act(e.id, "publish")}><UploadSimple size={15} className="mr-2" /> Publish</DropdownMenuItem>}
                        {e.status === "published" && <DropdownMenuItem onClick={() => act(e.id, "unpublish")}><EyeSlash size={15} className="mr-2" /> Unpublish</DropdownMenuItem>}
                        {e.status === "published" && <DropdownMenuItem asChild><Link to={`/events/${e.id}`}><Eye size={15} className="mr-2" /> View live</Link></DropdownMenuItem>}
                        <DropdownMenuItem onClick={() => act(e.id, "duplicate")}><Copy size={15} className="mr-2" /> Duplicate</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => act(e.id, "cancel")} className="text-destructive"><XCircle size={15} className="mr-2" /> Cancel</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
